import { useCallback, useEffect, useState } from 'react';
import apiFetch from '../utils/apiFetch';
import { emptyMemberNote } from '../utils/dashboard/commanderMemberNoteHelpers';

/**
 * 当前指挥员帐号下的队员备注（备注名 / 电话 / 描述），互不可见。
 */
export default function useCommanderMemberNotes(enabled = true) {
  const [notesByKey, setNotesByKey] = useState({});
  const [loading, setLoading] = useState(false);

  const reload = useCallback(async () => {
    if (!enabled) return;
    setLoading(true);
    try {
      const res = await apiFetch('/api/commander-member-notes');
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data?.success) {
        setNotesByKey({});
        return;
      }
      setNotesByKey(data.notes && typeof data.notes === 'object' ? data.notes : {});
    } catch (_) {
      setNotesByKey({});
    } finally {
      setLoading(false);
    }
  }, [enabled]);

  useEffect(() => {
    reload();
  }, [reload]);

  const saveNote = useCallback(async (memberKey, payload) => {
    const key = String(memberKey || '').trim();
    if (!key) throw new Error('缺少队员标识');
    const body = {
      remark_name: payload?.remark_name ?? '',
      phones: Array.isArray(payload?.phones) ? payload.phones : [],
      description: payload?.description ?? ''
    };
    const empty = !String(body.remark_name || '').trim()
      && body.phones.length === 0
      && !String(body.description || '').trim();
    const res = await apiFetch(
      `/api/commander-member-notes/${encodeURIComponent(key)}`,
      {
        method: empty ? 'DELETE' : 'PUT',
        headers: empty ? undefined : { 'Content-Type': 'application/json' },
        body: empty ? undefined : JSON.stringify(body)
      }
    );
    const data = await res.json().catch(() => ({}));
    if (!res.ok || !data?.success) {
      if (res.status === 404) {
        throw new Error('备注接口未加载（需重启后端 :5000）或路径不存在');
      }
      if (res.status === 500 && /不存在|does not exist|relation/i.test(String(data?.error || ''))) {
        throw new Error('备注表未创建，请先执行 commander_member_notes 迁移');
      }
      throw new Error(data?.error || `保存失败 (HTTP ${res.status})`);
    }
    const note = data.note || emptyMemberNote(key);
    setNotesByKey((prev) => {
      const next = { ...prev };
      const stillEmpty = !String(note.remark_name || '').trim()
        && !(Array.isArray(note.phones) && note.phones.length)
        && !String(note.description || '').trim();
      if (stillEmpty) delete next[key];
      else next[key] = note;
      return next;
    });
    return note;
  }, []);

  return { notesByKey, loading, reload, saveNote };
}
